import { useCallback, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, Receipt } from "lucide-react";
import { PRINT_CHOICES, PAPER_WIDTHS, loadPrintPref, savePrintPref, resolvePrintFormat } from "@/lib/printFormat";

// Format selector shown before printing a document. onPick(format) receives the printDoc() format id.
export function PrintFormatDialog({ open, onClose, onPick, docType = "document", title = "Print" }) {
  const [pref, setPref] = useState(() => loadPrintPref(docType));
  const pick = (choice) => {
    const next = { ...pref, choice };
    savePrintPref(docType, next); setPref(next);
    onPick(resolvePrintFormat(next));
    onClose();
  };
  const setWidth = (width) => { const next = { ...pref, width }; savePrintPref(docType, next); setPref(next); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="print-format-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">{title}</DialogTitle>
          <DialogDescription>Choose how this document should be printed.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          {PRINT_CHOICES.map((c) => (
            <button key={c.id} type="button" onClick={() => pick(c.id)} data-testid={`print-format-${c.id}`}
              className={`w-full text-left rounded-xl border p-3 flex items-start gap-3 transition-colors hover:bg-secondary ${pref.choice === c.id ? "border-emerald-500/60 bg-emerald-500/5" : "border-border"}`}>
              {c.id === "thermal" ? <Receipt className="w-5 h-5 mt-0.5 text-emerald-600 shrink-0" /> : <FileText className="w-5 h-5 mt-0.5 text-sky-600 shrink-0" />}
              <span className="min-w-0">
                <span className="block font-semibold text-sm">{c.label}</span>
                <span className="block text-xs text-muted-foreground">{c.hint}</span>
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between gap-2 pt-1" data-testid="print-paper-width">
          <span className="text-xs text-muted-foreground">Receipt paper width</span>
          <div className="flex gap-1">
            {PAPER_WIDTHS.map((w) => (
              <Button key={w.id} type="button" size="sm" variant={pref.width === w.id ? "default" : "outline"}
                className={`h-7 px-2.5 text-xs ${pref.width === w.id ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                onClick={() => setWidth(w.id)} data-testid={`print-width-${w.id}`}>{w.label}</Button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// usePrintFormat("quotation") -> { requestPrint(printFn), dialog }
// requestPrint opens the selector; the chosen printDoc() format is passed to printFn(format).
export function usePrintFormat(docType, title) {
  const [open, setOpen] = useState(false);
  const pending = useRef(null);
  const requestPrint = useCallback((printFn) => { pending.current = printFn; setOpen(true); }, []);
  const onPick = (format) => { const fn = pending.current; pending.current = null; if (fn) fn(format); };
  const dialog = <PrintFormatDialog open={open} onClose={() => setOpen(false)} onPick={onPick} docType={docType} title={title} />;
  return { requestPrint, dialog };
}
